import React, { useEffect, useMemo, useState } from 'react';
import { demeterApi } from '../../../../Apis/Apis';
import { ListCommodityPricesByFrequencyResponse } from '../../../../Generated/Raven-Demeter';
import useCacheThenApi from '../../../Apis/Hooks/useCacheThenApiHook';
import PageLoadingSpinner from '../../../Components/LoadingSpinner/PageLoadingSpinner';
import CacheKeys from '../../../Services/Cache/CacheKeys';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { PhysicalPricesCompositeModel } from '../../PhysicalPrices/PhysicalPricesPageDefinitions';
import { IWithPageElementProps } from '../DashboardCustomizationDefinitions';
import { IDashboardPricesTableProps } from '../DashboardTables/DashboardTableFromPageElement';

const withPhysicalPricesTableFromPageElement = (WrappedComponent: React.ComponentType<IDashboardPricesTableProps>): React.FC<IWithPageElementProps> => {
    const Wrapped: React.FC<IWithPageElementProps> = (props) => {
        if (!props.pageElement) {
            return <> </>;
        }

        const [translations] = useLanguage();
        const [physicalPriceRows, setPhysicalPriceRows] = useState<PhysicalPricesCompositeModel[]>([]);
        const [hasApiResponded, setHasApiResponded] = useState(false);

        const { region, commodity, pageType, currency, unitOfMeasure, dataFrequency, commodityDisplayName, subCommodityDisplayName, regionDisplayName } =
            props.pageElement;

        const cacheKey = useMemo(
            () => `${CacheKeys.GetDemeterPrices}_${dataFrequency}_${region}_${commodity}_${currency}_${unitOfMeasure}_dashboard`,
            [region, commodity, dataFrequency, currency, unitOfMeasure],
        );

        const [, , physicalPricesResponse] = useCacheThenApi<ListCommodityPricesByFrequencyResponse>(cacheKey, () => {
            if (!region || !commodity || !dataFrequency) {
                return null;
            }
            return demeterApi.listCommodityPricesByFrequency(
                region as string,
                commodity as string,
                dataFrequency as string,
                undefined,
                currency ?? undefined,
                unitOfMeasure ?? undefined,
            );
        });

        useEffect(() => {
            if (physicalPricesResponse === undefined) {
                return;
            }
            setHasApiResponded(true);
            if (physicalPricesResponse?.rows && physicalPricesResponse.rows.length > 0) {
                const rows = physicalPricesResponse.rows.map((row, index) => ({
                    ...row,
                    id: `${index}`,
                    rowIndex: index,
                    selected: false,
                }));
                setPhysicalPriceRows(rows);
            }
        }, [physicalPricesResponse]);

        const regionPart = regionDisplayName ?? (region && translations.region[region]) ?? '';
        const commodityPart = (subCommodityDisplayName ?? commodityDisplayName ?? '') as string;
        const pageTypeSuffix = (pageType && translations.pageType[pageType]) || 'Physical Prices';
        const title = [regionPart, commodityPart, pageTypeSuffix].filter(Boolean).join(' ');

        if (!hasApiResponded) {
            return <PageLoadingSpinner />;
        }

        const customTitle = typeof props.customTitle === 'string' ? props.customTitle : undefined;

        return (
            <WrappedComponent
                tableType="physical"
                tableData={physicalPriceRows}
                title={title}
                customTitle={customTitle}
                dragIndicatorIcon={props.dragIndicatorIcon}
                isInDashboardEditMode={props.isInDashboardEditMode}
                maximizeMinimizeButton={props.maximizeMinimizeButton}
                useDynamicHeight={props.useDynamicHeight}
                handleMoreOptionsSelection={props.handleMoreOptionsSelection}
            />
        );
    };

    return Wrapped;
};

export default withPhysicalPricesTableFromPageElement;
